import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, Trash2, Eye, Download, FileText, Image, FileSpreadsheet, File, Paperclip } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ConfirmacionAdminModal } from "./ConfirmacionAdminModal";
import { VisualizadorEvidencia } from "./VisualizadorEvidencia";

export interface EvidenciaArchivo {
  id: string;
  nombre: string;
  tipo: string;
  tamano: number;
  url?: string;
  fechaCarga?: string;
}

interface EvidenciasManagerProps {
  evidencias: EvidenciaArchivo[];
  onEvidenciasUpdate: (evidencias: EvidenciaArchivo[]) => void;
  readOnly?: boolean;
}

const EvidenciasManager = ({ 
  evidencias, 
  onEvidenciasUpdate, 
  readOnly = false 
}: EvidenciasManagerProps) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [evidenciaAEliminar, setEvidenciaAEliminar] = useState<string | null>(null);
  const [visorOpen, setVisorOpen] = useState(false);
  const [evidenciaSeleccionada, setEvidenciaSeleccionada] = useState<EvidenciaArchivo | null>(null);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const nuevasEvidencias: EvidenciaArchivo[] = [];
    
    Array.from(files).forEach(file => {
      // Validar tipos de archivo permitidos
      const tiposPermitidos = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'image/jpeg',
        'image/png',
        'image/gif',
        'image/webp'
      ];

      if (!tiposPermitidos.includes(file.type)) {
        toast({
          title: "Archivo no permitido",
          description: `El archivo ${file.name} no está en un formato permitido.`,
          variant: "destructive"
        });
        return;
      }

      // Validar tamaño (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "Archivo muy grande",
          description: `El archivo ${file.name} excede el tamaño máximo de 10MB.`,
          variant: "destructive"
        });
        return;
      }

      const nuevaEvidencia: EvidenciaArchivo = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        nombre: file.name,
        tipo: file.type,
        tamano: file.size,
        fechaCarga: new Date().toISOString(),
        url: URL.createObjectURL(file) // Simular URL del archivo
      };

      nuevasEvidencias.push(nuevaEvidencia);
    });

    if (nuevasEvidencias.length > 0) {
      const evidenciasActualizadas = [...evidencias, ...nuevasEvidencias];
      onEvidenciasUpdate(evidenciasActualizadas);
      
      toast({
        title: "Evidencias cargadas",
        description: `Se cargaron ${nuevasEvidencias.length} archivo(s) correctamente.`
      });
    }

    // Limpiar el input
    event.target.value = '';
  };

  const handleSolicitarEliminar = (id: string) => {
    setEvidenciaAEliminar(id);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmarEliminar = () => {
    if (evidenciaAEliminar) {
      const evidenciasActualizadas = evidencias.filter(e => e.id !== evidenciaAEliminar);
      onEvidenciasUpdate(evidenciasActualizadas);
      
      toast({
        title: "Evidencia eliminada",
        description: "La evidencia se ha eliminado correctamente."
      });

      setEvidenciaAEliminar(null);
    }
  };

  const handleVisualizarEvidencia = (evidencia: EvidenciaArchivo) => {
    setEvidenciaSeleccionada(evidencia);
    setVisorOpen(true);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (tipo: string) => {
    if (tipo.includes('pdf')) return <FileText className="w-4 h-4 text-red-500" />;
    if (tipo.includes('image')) return <Image className="w-4 h-4 text-blue-500" />;
    if (tipo.includes('spreadsheet') || tipo.includes('excel')) return <FileSpreadsheet className="w-4 h-4 text-green-500" />;
    return <File className="w-4 h-4 text-gray-500" />;
  };

  const getFileTypeBadge = (tipo: string) => {
    if (tipo.includes('pdf')) return <Badge variant="destructive">PDF</Badge>;
    if (tipo.includes('image')) return <Badge variant="default">Imagen</Badge>;
    if (tipo.includes('word')) return <Badge variant="secondary">Word</Badge>;
    if (tipo.includes('spreadsheet') || tipo.includes('excel')) return <Badge variant="outline">Excel</Badge>;
    return <Badge variant="outline">Archivo</Badge>;
  };

  const formatFecha = (fechaISO: string) => {
    const fecha = new Date(fechaISO);
    return fecha.toLocaleString('es-CO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Evidencias ({evidencias.length})
          </CardTitle>
          {!readOnly && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button 
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip className="w-4 h-4 mr-2" />
                Adjuntar Archivos
              </Button>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {evidencias.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Upload className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No hay evidencias cargadas</p>
            {!readOnly && (
              <p className="text-sm">Haz clic en "Subir Evidencias" para agregar archivos</p>
            )}
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Tamaño</TableHead>
                  <TableHead>Fecha de Carga</TableHead>
                  <TableHead className="w-24">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {evidencias.map((evidencia) => (
                  <TableRow key={evidencia.id}>
                    <TableCell>
                      {getFileIcon(evidencia.tipo)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {evidencia.nombre}
                    </TableCell>
                    <TableCell>
                      {getFileTypeBadge(evidencia.tipo)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatFileSize(evidencia.tamano)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatFecha(evidencia.fechaCarga)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          title="Visualizar"
                          onClick={() => handleVisualizarEvidencia(evidencia)}
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          title="Descargar"
                          onClick={() => {
                            if (evidencia.url) {
                              const link = document.createElement('a');
                              link.href = evidencia.url;
                              link.download = evidencia.nombre;
                              link.click();
                            }
                          }}
                        >
                          <Download className="h-3 w-3" />
                        </Button>
                        {!readOnly && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            title="Eliminar (requiere admin)"
                            onClick={() => handleSolicitarEliminar(evidencia.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Modal de confirmación con contraseña de administrador */}
      <ConfirmacionAdminModal
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        onConfirm={handleConfirmarEliminar}
        titulo="Eliminar Evidencia"
        descripcion="Para eliminar esta evidencia, debe confirmar su identidad como administrador."
      />

      {/* Modal de visualización */}
      <VisualizadorEvidencia
        open={visorOpen}
        onOpenChange={setVisorOpen}
        evidencia={evidenciaSeleccionada}
      />
    </Card>
  );
};

export default EvidenciasManager;